import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePatients } from "@/lib/usePatients";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/clinical/PageHeader";
import { ReviewNote } from "@/components/clinical/ReviewNote";
import { RelevanceBadge } from "@/components/clinical/PhaseBadge";
import { EmptyState } from "@/components/clinical/EmptyState";
import { runInteractionEngine } from "@/domain/interactionEngine";
import { buildSessionContext } from "@/domain/sessionContextEngine";
import { getRuntimeKnowledgeBase } from "@/lib/pharmacology/runtimeKnowledge";
import { AdvancedCurveExplorer } from "@/components/clinical/AdvancedCurveExplorer";
import { Zap } from "lucide-react";
import { useState } from "react";
import { PRIMARY_PATIENT_ID } from "@/lib/clinical/singlePatientMode";

export default function Interactions() {
  const { data: patients = [] } = usePatients();
  const [patientId, setPatientId] = useState(PRIMARY_PATIENT_ID);
  const [hours, setHours] = useState(72);
  const { data: meds = [] } = useQuery({
    queryKey: ["int-meds", patientId], enabled: !!patientId,
    queryFn: async () => (await supabase.from("patient_medications").select("*, substances(name)").eq("patient_id", patientId).eq("status", "ativo")).data ?? [],
  });
  const { data: doses = [] } = useQuery({
    queryKey: ["int-doses", patientId, hours], enabled: !!patientId,
    queryFn: async () => {
      // 30 dias de contexto: cobre manutenção/carry-over e riscos toxicológicos tardios
      // vinculados a perfis de amostra (p. ex. alterações hematológicas/vasculares).
      // A janela da curva continua definida pelo snapshot; este período é para contexto.
      const t = new Date(Date.now() - Math.max(hours, 720) * 3600_000).toISOString();
      return (await (supabase as any)
        .from("medication_dose_logs")
        .select("*, substances(*), substance_formulations(*)")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .gte("actual_time", t)
        .order("actual_time", { ascending: true })).data ?? [];
    },
  });
  const { data: subUse = [] } = useQuery({
    queryKey: ["int-subuse", patientId, hours], enabled: !!patientId,
    queryFn: async () => {
      const t = new Date(Date.now() - Math.max(hours, 720) * 3600_000).toISOString();
      return (await supabase.from("substance_use_logs").select("substance_name, used_at").eq("patient_id", patientId).gte("used_at", t)).data ?? [];
    },
  });

  const now = new Date();
  const currentPatient = patients.find((p: any) => p.id === patientId) as any;
  const bodyWeightKg = Number(currentPatient?.weight_kg) > 0 ? Number(currentPatient.weight_kg) : null;
  const doseKeys = new Set((doses as any[]).map((d) => String(d.substance_name ?? "").toLowerCase()));
  const estimatedMaintenance = (meds as any[])
    .filter((m) => {
      const name = m.substances?.name ?? m.free_text_name ?? m.brand_name ?? "";
      const freq = String(m.frequency ?? m.dosing_frequency ?? "");
      return m.current_dose != null &&
        !/prn|sos|se necessário|se necessario|eventual/i.test(freq) &&
        !doseKeys.has(String(name).toLowerCase());
    })
    .map((m) => ({
      substance_name: m.substances?.name ?? m.free_text_name ?? m.brand_name ?? "Medicação",
      actual_time: now, dose_amount: m.current_dose, dose_unit: m.dose_unit,
      formulation: m.formulation, log_type: "prescricao_ativa_estimada",
    }));
  const temporalDoses = [...(doses as any[]), ...estimatedMaintenance];
  const currentSnapshot = patientId ? buildSessionContext({
    allDoses: temporalDoses as any[], sessionDoses: temporalDoses as any[],
    at: now, knowledge: getRuntimeKnowledgeBase(),
    bodyWeightKg,
  }) : null;
  const currentStates = currentSnapshot?.states ?? [];
  const dominantStates = [...currentStates]
    .filter((s: any) => Number(s.effect0to100 ?? 0) >= 25)
    .sort((a: any, b: any) => Number(b.effect0to100 ?? 0) - Number(a.effect0to100 ?? 0));
  const intensityWord = (v: number) => v >= 70 ? "marcada" : v >= 40 ? "moderada" : "baixa";
  const clinicalNow = dominantStates.length
    ? dominantStates.slice(0, 5).map((s: any) => s.name + " · " + s.phase + " · contribuição " + intensityWord(Number(s.effect0to100))).join(" · ")
    : "Nenhuma exposição com contribuição farmacológica relevante foi modelada agora.";
  const residualStates = currentStates.filter((s: any) => s.phase === "residual" || s.phase === "washout" || s.carryOver);
  const clinicalNext = residualStates.length
    ? "Atenção à cauda/carry-over de " + residualStates.slice(0, 4).map((s: any) => s.name).join(", ") + "; a contribuição atual é menor que um pico, mas pode afetar sono, sedação, ativação ou a leitura da resposta."
    : "Sem cauda residual relevante identificada pela modelagem atual.";

  const interactions = patientId ? runInteractionEngine({
    exposureDoses: temporalDoses,
    bodyWeightKg,
    activeMedications: meds.map((m: any) => ({
      name: m.substances?.name ?? m.free_text_name ?? m.brand_name ?? "",
      frequency: m.frequency ?? null,
      currentDose: m.current_dose ?? null,
    })),
    recentDoses: doses.map((d: any) => ({ substanceName: d.substance_name, actualTime: d.actual_time, logType: d.log_type, doseAmount: d.dose_amount, caffeineMg: d.caffeine_near_dose_mg ?? d.caffeine_amount, sleepDeprivation: d.sleep_deprivation_at_dose_0_10 })),
    recentSubstanceUse: subUse.map((s: any) => ({ substanceName: s.substance_name, usedAt: s.used_at })),
  }) : [];

  return (
    <div className="space-y-6">
      <PageHeader title="Interações contextuais" description="Cruza fármacos, redose, duas doses da mesma substância, cafeína, sono e uso de substâncias. Não é só interação genérica de bula." />
      <ReviewNote />
      <Card className="rounded-2xl border-primary/20 bg-primary/5 p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div><div className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Fotografia clínica · agora</div>
          <div className="mt-1 text-sm font-medium">{clinicalNow}</div></div>
          <div className="text-[10px] text-muted-foreground">Snapshot temporal compartilhado</div>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="rounded-xl border bg-background/60 p-3"><div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Próximas horas / residual</div><div className="mt-1 text-xs leading-relaxed">{clinicalNext}</div></div>
          <div className="rounded-xl border bg-background/60 p-3"><div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Exposições consideradas</div>
            <div className="mt-1 text-xs leading-relaxed">{meds.length ? meds.length + " prescrição(ões) ativa(s) + " : ""}{doses.length} dose(s) efetivamente registrada(s) + {subUse.length} uso(s) não prescrito(s). <span className="text-muted-foreground">Prescrição ativa sem dose registrada é exposição estimada, não adesão confirmada.</span></div>
          </div>
        </div>
      </Card>
      <Card className="p-4">
        <div className="grid md:grid-cols-[1fr_auto_auto] gap-3 items-end mb-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Paciente</Label>
            <select className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[240px]" value={patientId} onChange={(e) => setPatientId(e.target.value)}>
              <option value="">Selecione…</option>
              {patients.map((p: any) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Janela de doses</Label>
            <select className="h-10 px-3 rounded-md border border-input bg-background text-sm" value={hours} onChange={(e) => setHours(Number(e.target.value))}>
              <option value={24}>24 h</option>
              <option value={48}>48 h</option>
              <option value={72}>72 h</option>
              <option value={168}>7 dias</option>
            </select>
          </div>
          {patientId && <div className="text-xs text-muted-foreground">{doses.length} doses · {subUse.length} usos/substâncias</div>}
        </div>
        {!patientId ? <EmptyState icon={<Zap className="h-5 w-5" />} title="Selecione um paciente" /> :
         interactions.length === 0 ? <div className="text-sm text-muted-foreground">Nenhuma interação contextual relevante nessa janela. Se houver redose, confirme se o registro está como log_type redose e com horário real.</div> : (
          <ul className="space-y-3">
            {interactions.map((it, i) => (
              <li key={i} className="border-l-2 border-warning pl-3">
                <div className="flex items-center gap-2"><RelevanceBadge relevance={it.relevance} /><span className="text-sm font-medium">{it.summary}</span></div>
                {it.detail && <div className="text-xs text-muted-foreground mt-1">{it.detail}</div>}
                {it.mechanism && <div className="text-xs mt-1"><span className="text-muted-foreground">Mecanismo:</span> {it.mechanism}</div>}
                {it.monitor && it.monitor.length > 0 && <div className="text-xs mt-1"><span className="text-muted-foreground">Monitorar:</span> {it.monitor.join(", ")}</div>}
                {it.action && <div className="text-xs mt-1"><span className="text-muted-foreground">Ação:</span> {it.action}</div>}
                {it.whyNow && <details className="mt-2 rounded-md bg-muted/40 p-2"><summary className="cursor-pointer text-[11px] font-medium">Por que está ativo agora?</summary><div className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{it.whyNow}</div></details>}
                {it.temporalBasis && <div className="text-[10px] mt-1 text-muted-foreground">Base temporal: {it.temporalBasis}</div>}
                {it.phenotype?.length ? <div className="text-[10px] mt-1 text-muted-foreground">Fenótipo: {it.phenotype.join(", ")}</div> : null}
                {it.redFlags?.length ? <div className="text-[10px] mt-1 text-destructive">Red flags: {it.redFlags.join(" · ")}</div> : null}
                {it.evidence && <div className="text-[10px] mt-1 text-muted-foreground">Fonte: {it.evidence}</div>}
                <div className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wide">Confiança {it.confidence}</div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {currentSnapshot?.states?.length ? <Card className="p-4 space-y-3 border-primary/20"><div className="text-sm font-medium">Estado farmacológico atual · todas as exposições modeladas</div><div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">{currentSnapshot.states.map((state) => <div key={state.key} className="rounded-lg border p-2.5 text-xs"><div className="font-medium">{state.name}</div><div className="text-muted-foreground mt-1">Fase: <b className="text-foreground">{state.phase}</b> · contribuição {intensityWord(state.effect0to100)}</div><div className="text-muted-foreground">Carga residual: {state.body ? `${(state.body.remainingFraction*100).toFixed(0)}%` : "não modelada"}</div><div className="text-muted-foreground">{state.tolerance.label}</div></div>)}</div></Card> : null}
      {patientId && doses.length > 0 && <AdvancedCurveExplorer doses={doses as any} title="Curva para interpretar interações por fase" />}
    </div>
  );
}
